import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useGiveSessionFeedbackMutation } from 'autogen/swagger/all/hooks';
import { Feedback } from 'autogen/translation-keys/trans-website-comai';
import { Button } from 'components/Button';
import DrawerView from 'components/generic/Drawer/DrawerView';
import { closeAllDrawers } from 'components/generic/Drawer/hooks/useDrawer';
import InteractiveRatingStars from 'components/generic/ratingStars/InteractiveRatingStars';
import { Typography } from 'components/generic/typography';
import Icon from 'components/Icon';
import FlexColumn from 'components/Layout/components/Flex/components/FlexColumn';

const Wrapper = styled(FlexColumn)`
    width: 100%;
    max-width: 350px;
    margin: auto;
    text-align: center;
    text-wrap: balance;
`;

const StyledButton = styled(Button)`
    width: fit-content;
    padding-inline: ${({ theme }) => theme.Spacing40};
`;

type FeedBackDrawerProps = {
    drawerId: string;
};

const StyledIcon = styled(Icon)`
    fill: ${({ theme }) => theme.ColorTextPrimaryInverted};
`;

const FeedBackDrawer: React.FC<FeedBackDrawerProps> = ({ drawerId }) => {
    const [rating, setRating] = useState<number | null>(null);
    const { mutateAsync: giveFeedback } = useGiveSessionFeedbackMutation();
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const { t } = useTranslation();
    const THANK_YOU_DISPLAY_TIME_MS = 1200 as const;

    const sendFeedback = () => {
        if (!rating) return;
        if (rating < 1 || rating > 5) return;

        setHasSubmitted(true);

        setTimeout(() => {
            closeAllDrawers();
        }, THANK_YOU_DISPLAY_TIME_MS);

        giveFeedback({ params: { rating } });
    };
    const dismiss = () => {
        closeAllDrawers();
    };

    return (
        <DrawerView id={drawerId} position="right" fullscreen>
            <Wrapper justifyContent="center">
                <FlexColumn gap="32px" alignItems="center">
                    <FlexColumn gap="8px" alignItems="center">
                        <Typography.Headline size="XL">{t(Feedback.Headline)}</Typography.Headline>
                        <Typography.Body>{t(Feedback.Body)}</Typography.Body>
                    </FlexColumn>
                    <InteractiveRatingStars onRatingSelect={(rating) => setRating(rating)} />
                    <FlexColumn gap="8px" alignItems="center">
                        <StyledButton disabled={!rating} size="lg" onClick={sendFeedback}>
                            {hasSubmitted ? <StyledIcon type="Checkmark" size={24} /> : t(Feedback.Send)}
                        </StyledButton>
                        <StyledButton variant="ghost" size="lg" onClick={dismiss}>
                            {t(Feedback.Dismiss)}
                        </StyledButton>
                    </FlexColumn>
                </FlexColumn>
            </Wrapper>
        </DrawerView>
    );
};

export default FeedBackDrawer;
